import { FileUploader } from 'react-drag-drop-files';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, FilledInput, IconButton, Typography } from '@mui/material';
import PreviewUpload from './PreviewUpload';
import CloseIcon from '@mui/icons-material/Close';
import { MAX_FILE_SIZE_BYTES } from '../../constants/numbers';
import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useState } from 'react';
interface IProps {
	type?: 'image' | 'video' | 'media' | 'file';
	text: string;
	multiple?: boolean;
	setFiles: React.Dispatch<React.SetStateAction<(File | string)[]>>;
	files: (File | string)[];
	className?: string;
	editable?: boolean;
	IsSerialStory?: boolean;
	isPost?: boolean;
	data?: { image: File; caption: string; type: string; dataType: string }[];
	setData?: Dispatch<SetStateAction<{ image: File; caption: string; type: string; dataType: string }[]>>;
	caption?: string;
	showHoverPopover?: boolean;
	popoverContent?: React.ReactNode;
	onSelectedImage?: (file: File) => void;
}

const typesFile = {
	image: ['jpg', 'png', 'jpeg', 'webp', 'jfif', 'svg'],
	video: ['mp4'],
	media: ['jpg', 'png', 'jpeg', 'webp', 'jfif', 'mp4'],
	file: undefined,
};

export default function Upload({ onSelectedImage, showHoverPopover = false, type = 'image', text, data = [], setData, caption = '', multiple = false, setFiles, files, className = '', editable = true }: IProps) {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
		if (showHoverPopover) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handlePopoverClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);
	console.log(open);

	const handleChange = (file: File | FileList) => {
		if (!file || !editable) return;

		const newFiles = file instanceof File ? [file] : [...file];

		// Check for files larger than 3MB
		const oversizedFiles = newFiles.filter((f: File) => f.size > MAX_FILE_SIZE_BYTES);
		if (oversizedFiles.length > 0) {
			alert(t('max_upload_size_warning'));
		}

		// Filter out files larger than 3MB
		const validFiles = newFiles.filter((f: File) => f.size <= MAX_FILE_SIZE_BYTES);

		setFiles((oldFiles: (File | string)[]) => [...oldFiles, ...validFiles]);
		let fileType = validFiles[0].type.split('/')[0];
		if (setData) {
			setData(prevData => [...prevData, { image: validFiles[0], caption, type: fileType, dataType: 'image' }]);
		}
		if (onSelectedImage && validFiles[0]) {
			onSelectedImage(validFiles[0]);
		}
	};

	const handleRemoveFile = (deletedFile: File | string) => (e: React.MouseEvent<HTMLButtonElement>) => {
		if (!editable) return;
		e.preventDefault();
		e.stopPropagation();

		const filteredFiles = files.filter(file => file !== deletedFile);
		setFiles(filteredFiles.length > 0 ? filteredFiles : []);
		const filteredData = data?.filter(file => file.image !== deletedFile);
		if (setData) {
			setData(filteredData ? filteredData : []);
		}
	};

	// const acceptTypesText = type === 'image' ? `SVG, PNG, JPG or GIF (max. ${MAX_FILE_SIZE_MB}MB)` : type === 'video' ? `MP4 (max. ${MAX_FILE_SIZE_MB}MB)` : type === 'media' ? `SVG, PNG, JPG, MP4 (max. ${MAX_FILE_SIZE_MB}MB)` : `(max. ${MAX_FILE_SIZE_MB}MB)`;

	return (
		<FileUploader multiple={multiple} classes={`cursor-pointer border-dashed border border-action-active rounded p-2 pt-6 min-h-32 relative bg-action-hover overflow-hidden ${className}`} handleChange={handleChange} component={<FilledInput />} name="file" minSize={0} types={typesFile[type]}>
			<Box>
				<div onMouseEnter={handlePopoverOpen} onMouseLeave={handlePopoverClose} className={`center flex w-full flex-col items-center justify-center gap-1 text-text-secondary`}>
					<CloudUploadIcon />
					<Typography className="text-text-primary">{text}</Typography>
					<Typography className="px-2 text-center text-sm text-text-disabled">{t('guide_upload_file')}</Typography>
				</div>
				{files.length > 0 && (
					<div className="mt-4 flex min-h-14 justify-center gap-2 overflow-x-auto">
						{files.map((file: File | string, index) => (
							<Box key={index} className="relative">
								<IconButton className="absolute top-0 z-50 p-0" onClick={handleRemoveFile(file)}>
									<CloseIcon className="rounded-full bg-error-main p-1 text-background-paper" />
								</IconButton>
								<PreviewUpload file={file} type={type} />
							</Box>
						))}
					</div>
				)}
			</Box>
		</FileUploader>
	);
}
